import { Dispatch, forwardRef, SetStateAction, useImperativeHandle, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { FormikProps } from 'formik'

import { CampaignPublicActions } from 'primsell-shared-layer/features'
import { Button } from 'primsell-shared-layer/components'

import { IBuyFormValues, useBuyFormController } from './buy-form.controller'

import * as S from './buy-form.styles'

interface BuyFormProps {
  amount: IBuyFormValues['amount']
  setAmount: Dispatch<SetStateAction<IBuyFormValues['amount']>>
  isPaymentSessionExpired: boolean
  handleRenewPaymentSession: () => void
}

const BuyForm = forwardRef<FormikProps<IBuyFormValues> | null, BuyFormProps>(
  ({ amount, setAmount, isPaymentSessionExpired, handleRenewPaymentSession }, externalRef) => {
    const dispatch = useDispatch()
    const { code } = useParams()
    const internalRef = useRef<FormikProps<IBuyFormValues> | null>(null)
    useImperativeHandle(externalRef, () => internalRef.current as FormikProps<IBuyFormValues>)

    const {
      formik,
      isSubmitDisabled,
      isInputDisabled,
      isAmountFieldDisabled,
      isCampaignLoyalty,
      collectionName,
      walletAddress,
    } = useBuyFormController({
      isPaymentSessionExpired,
      amount,
      internalRef,
      handleRenewPaymentSession,
    })

    const handleSubmit = () => {
      code && dispatch(CampaignPublicActions.getPublicCampaignDetailsByCodeRequest({ code }))
      formik.handleSubmit()
    }

    return (
      <S.BuyFormWrapper>
        <form onSubmit={(e) => e.preventDefault()}>
          <S.Input
            label="Amount"
            placeholder="Enter amount"
            disabled={isInputDisabled || isAmountFieldDisabled}
            isRequired
            error={formik.errors['amount']}
            {...formik.getFieldProps('amount')}
            onChange={(e) => {
              setAmount(e.target.value)
              formik.setFieldValue('amount', e.target.value)
            }}
            inputTooltip={{
              offset: 14,
              tooltipStyle: { width: '290px', textAlign: 'center' },
              place: 'bottom',
              content: `Please note there is only 1 ${collectionName} NFT that you can purchase per your wallet.`,
              disabled: !isCampaignLoyalty || !walletAddress,
            }}
          />
          <S.Input
            label="Email"
            placeholder="Enter your email"
            disabled={isInputDisabled}
            isRequired
            error={formik.errors['email']}
            {...formik.getFieldProps('email')}
          />
          <S.Input
            label="First Name"
            placeholder="Enter your name"
            disabled={isInputDisabled}
            isRequired
            error={formik.errors['firstName']}
            {...formik.getFieldProps('firstName')}
          />
          <S.Input
            label="Last Name"
            placeholder="Enter your last name"
            disabled={isInputDisabled}
            isRequired
            error={formik.errors['lastName']}
            {...formik.getFieldProps('lastName')}
          />
          <Button
            variant="outlined"
            color="grey"
            fullWidth
            type="submit"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          >
            Proceed
          </Button>
        </form>
      </S.BuyFormWrapper>
    )
  }
)

BuyForm.displayName = 'BuyForm'

export { BuyForm }
